import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Button from '../../../component/button/button';
import CustomInput from '../../../component/CustomInput/CustomInput';
import SubjectImageUpload from '../SubjectImage';
import LevelLabelEditor from './LevelLabelEditor';
import { SubjectErrorBags, TeacherData } from '../SubjectInterface';
import { listTeacher } from '../../../api/teacher';
import { updateSubjectData } from '../../../api/subject';
import {
  successMessage,
  errorMessageShow,
  LoaderComponent,
} from '../../../utils/Helper';
import {
  SubjectDetail,
  toSubjectDetail,
  validateLevelLabels,
  hasLabelErrors,
  DEFAULT_LEVEL_LABELS,
} from './hierarchyUtils';

const isDefaultLabels = (labels: string[]): boolean =>
  Array.isArray(labels) &&
  labels.length === DEFAULT_LEVEL_LABELS.length &&
  DEFAULT_LEVEL_LABELS.every((label, i) => labels[i] === label);

interface SubjectOverviewPanelProps {
  subject: SubjectDetail;
  onSubjectChange: (subject: SubjectDetail) => void;
}

interface Option {
  value: string;
  label: string;
}

const SubjectOverviewPanel: React.FC<SubjectOverviewPanelProps> = ({
  subject,
  onSubjectChange,
}) => {
  const [teacherData, setTeacherData] = useState<TeacherData[]>([]);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSubmittingRef = useRef(false);

  const [title, setTitle] = useState(subject.title);
  const [labels, setLabels] = useState<string[]>(subject.level_labels);
  const [useDefaultLabels, setUseDefaultLabels] = useState(() =>
    isDefaultLabels(subject.level_labels)
  );
  const [teacherIds, setTeacherIds] = useState<string[]>(
    subject.teachers.map(t => t.id)
  );
  const [image, setImage] = useState<any>(subject.image || '');
  const [titleError, setTitleError] = useState('');
  const [teacherError, setTeacherError] = useState('');
  const [labelErrors, setLabelErrors] = useState<string[]>([]);
  const [imageError, setImageError] = useState<SubjectErrorBags>({});

  useEffect(() => {
    setTitle(subject.title);
    setLabels(subject.level_labels);
    setUseDefaultLabels(isDefaultLabels(subject.level_labels));
    setTeacherIds(subject.teachers.map(t => t.id));
    setImage(subject.image || '');
  }, [subject]);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setTeacherLoading(true);
        const response = await listTeacher(1, '', 100);
        setTeacherData(response?.data?.data?.teachers || []);
      } catch (error) {
        console.error('Error fetching teachers:', error);
      } finally {
        setTeacherLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  const selectedTeachers: Option[] = teacherIds
    .map(id => {
      const teacher = teacherData.find(t => t.id === id);
      return teacher
        ? { value: teacher.id, label: `${teacher.first_name} ${teacher.last_name}` }
        : null;
    })
    .filter((o): o is Option => o !== null);

  const handleSave = async () => {
    if (isSubmittingRef.current || saving) return;

    const effectiveLabels = useDefaultLabels ? [...DEFAULT_LEVEL_LABELS] : labels;
    const nextLabelErrors = useDefaultLabels ? [] : validateLevelLabels(labels);
    let hasError = false;

    if (title.trim().length < 3) {
      setTitleError('Title must be at least 3 characters long.');
      hasError = true;
    }
    if (teacherIds.length === 0) {
      setTeacherError('Please select at least one teacher.');
      hasError = true;
    }
    if (hasLabelErrors(nextLabelErrors)) {
      setLabelErrors(nextLabelErrors);
      hasError = true;
    }
    if (hasError) {
      errorMessageShow('Please fix the highlighted fields.');
      return;
    }

    try {
      isSubmittingRef.current = true;
      setSaving(true);
      const formData = new FormData();
      formData.append('title', title.trim());
      teacherIds.forEach(id => formData.append('teachers', id));
      effectiveLabels.forEach(label =>
        formData.append('level_labels', label.trim())
      );
      if (image instanceof File) {
        formData.append('image', image);
      }

      const response = await updateSubjectData(formData, subject.id);
      const updated = toSubjectDetail(response?.data?.data);
      onSubjectChange(updated);
      successMessage('Subject details updated.');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        errorMessageShow(
          error.response?.data?.message || 'An unexpected error occurred.'
        );
      } else {
        console.error(error);
      }
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="subject_overview_panel">
      <div className="subject_overview_main">
        <div className="subject_overview_image">
          <SubjectImageUpload
            inputChangeHandler={e => {
              const file = e.target.files ? e.target.files[0] : null;
              setImage(file);
            }}
            setFormData={() => {}}
            setErrorBags={setImageError}
            imagestring={typeof image === 'string' ? image : undefined}
          />
          {imageError.image && (
            <p className="text-red-500 text-xs mt-1">{imageError.image}</p>
          )}
        </div>

        <div className="subject_overview_fields">
          <div className="form-group mb-3">
            <label className="text-sm">
              Subject Title <span className="text-red-500">*</span>
            </label>
            <CustomInput
              value={title}
              name="title"
              placeholder="Subject title"
              onChange={e => {
                setTitle(e.target.value);
                setTitleError('');
              }}
              error={titleError}
              style={{ height: 42 }}
            />
          </div>

          <div className="form-group mb-3">
            <label className="text-sm">
              Assign Teachers <span className="text-red-500">*</span>
            </label>
            {teacherLoading ? (
              <LoaderComponent size="sm" label="Loading teachers…" />
            ) : (
              <Select
                isMulti
                value={selectedTeachers}
                options={teacherData.map(t => ({
                  value: t.id,
                  label: `${t.first_name} ${t.last_name}`,
                }))}
                onChange={selected => {
                  setTeacherIds((selected as Option[]).map(o => o.value));
                  setTeacherError('');
                }}
                placeholder="Select teacher(s)"
              />
            )}
            {teacherError && (
              <p className="text-red-500 text-xs mt-1">{teacherError}</p>
            )}
          </div>
        </div>
      </div>

      <div className="level_label_default_toggle">
        <label className="level_label_checkbox">
          <input
            type="checkbox"
            checked={useDefaultLabels}
            onChange={e => {
              const checked = e.target.checked;
              setUseDefaultLabels(checked);
              setLabels(checked ? [...DEFAULT_LEVEL_LABELS] : ['', '', '', '']);
              setLabelErrors([]);
            }}
          />
          <span>Use default level names (Topic, Subtopic, Chapter, Section)</span>
        </label>
      </div>

      {!useDefaultLabels && (
        <LevelLabelEditor
          labels={labels}
          errors={labelErrors}
          onChange={next => {
            setLabels(next);
            setLabelErrors([]);
          }}
        />
      )}

      <div className="subject_overview_actions">
        <Button
          text={saving ? 'Saving…' : 'Save changes'}
          buttonType="button"
          onClick={handleSave}
          disabled={saving}
          isLoading={saving}
          loadingText="Saving…"
          style={{ height: 44, fontSize: 14 }}
        />
      </div>
    </div>
  );
};

export default SubjectOverviewPanel;
